import { useState } from 'react';
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, KeyRound, Shield, AlertCircle } from '@/lib/icons';

import { useToast } from '@/hooks/use-toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

import { ButtonSpinner } from '@/components/feedback';
import { resetPassword } from '@/features/auth/api';

const createResetPasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      newPassword: z.string().min(6, t('auth.validation.passwordMinLength')),
      confirmPassword: z
        .string()
        .min(6, t('auth.validation.passwordMinLength')),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t('auth.validation.passwordsNotMatch'),
      path: ['confirmPassword'],
    });

type ResetPasswordFormValues = z.infer<
  ReturnType<typeof createResetPasswordSchema>
>;

export const Route = createFileRoute('/(auth)/reset-password')({
  component: ResetPasswordComponent,
  validateSearch: z.object({
    token: z.string().optional(),
  }),
});

function ResetPasswordComponent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { token } = useSearch({ from: '/(auth)/reset-password' });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetPasswordSchema = createResetPasswordSchema(t);

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const onSubmit = async (values: ResetPasswordFormValues) => {
    if (!token) {
      toast({
        title: t('auth.resetPassword.toast.invalidLinkTitle'),
        description: t('auth.resetPassword.toast.invalidLinkDescription'),
        variant: 'destructive',
      });
      return;
    }

    const result = await resetPassword(token, values.newPassword);

    if (!result.succeeded) {
      toast({
        title: t('auth.resetPassword.toast.errorTitle'),
        description: t('auth.resetPassword.toast.errorDescription'),
        variant: 'destructive',
      });
      return;
    }

    toast({
      title: t('auth.resetPassword.toast.successTitle'),
      description: t('auth.resetPassword.toast.successDescription'),
    });

    navigate({ to: '/login' });
  };

  // No token — invalid link
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-6">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-[40%] -right-[20%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-destructive/8 to-destructive/3 blur-3xl" />
        </div>
        <div className="w-full max-w-[420px] relative z-10">
          <div className="bg-[hsl(var(--surface-container-lowest)/0.85)] backdrop-blur-xl rounded-2xl border border-[hsl(var(--foreground)/0.06)] shadow-[0_24px_40px_-8px_hsl(213_20%_15%/0.08)] p-8 text-center space-y-6">
            <div className="flex justify-center">
              <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-destructive to-destructive/80 flex items-center justify-center shadow-lg shadow-destructive/20">
                <AlertCircle className="h-6 w-6 text-destructive-foreground" />
              </div>
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold tracking-tight">
                {t('auth.resetPassword.invalidLink.title')}
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('auth.resetPassword.invalidLink.description')}
              </p>
            </div>
            <Button
              onClick={() => navigate({ to: '/forgot-password' })}
              className="w-full h-11 rounded-xl font-semibold"
            >
              {t('auth.resetPassword.invalidLink.requestNewLink')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background relative overflow-hidden p-6">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -right-[20%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-primary/8 to-primary/3 blur-3xl animate-pulse" />
        <div className="absolute -bottom-[30%] -left-[15%] w-[50%] h-[70%] rounded-full bg-gradient-to-tr from-primary/6 to-transparent blur-3xl" />
      </div>

      <div className="w-full max-w-[420px] space-y-6 relative z-10">
        {/* Card */}
        <div className="bg-[hsl(var(--surface-container-lowest)/0.85)] backdrop-blur-xl rounded-2xl border border-[hsl(var(--foreground)/0.06)] shadow-[0_24px_40px_-8px_hsl(213_20%_15%/0.08)] p-8 space-y-6">
          {/* Icon */}
          <div className="flex justify-center">
            <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-lg shadow-primary/20">
              <KeyRound className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center space-y-2">
            <h2 className="text-xl font-bold tracking-tight">
              {t('auth.resetPassword.title')}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t('auth.resetPassword.description')}
            </p>
          </div>

          {/* Form */}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="newPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('auth.resetPassword.newPasswordLabel')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder={t(
                            'auth.resetPassword.newPasswordPlaceholder'
                          )}
                          className="h-11 rounded-xl bg-muted/50 border-border/50 focus-visible:ring-primary/30 pr-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 py-2 z-10 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={
                            showPassword ? 'Hide password' : 'Show password'
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-medium">
                      {t('auth.resetPassword.confirmPasswordLabel')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder={t(
                            'auth.resetPassword.confirmPasswordPlaceholder'
                          )}
                          className="h-11 rounded-xl bg-muted/50 border-border/50 focus-visible:ring-primary/30 pr-10"
                          {...field}
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-0 top-0 h-full px-3 py-2 z-10 hover:bg-transparent"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          aria-label={
                            showConfirmPassword
                              ? 'Hide password'
                              : 'Show password'
                          }
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
                          )}
                        </Button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                className="w-full h-11 rounded-xl font-semibold text-sm bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300"
                disabled={form.formState.isSubmitting}
              >
                {form.formState.isSubmitting ? (
                  <>
                    <ButtonSpinner className="mr-2" />
                    {t('common.loading.default')}
                  </>
                ) : (
                  t('auth.resetPassword.submitButton')
                )}
              </Button>
            </form>
          </Form>
        </div>

        {/* Security badge */}
        <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground/70">
          <Shield className="h-3.5 w-3.5" />
          <span>Secured with end-to-end encryption</span>
        </div>
      </div>
    </div>
  );
}
